import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Hash, ChevronDown, Sparkles, Archive, RotateCcw, Lock } from "lucide-react";
import { PremiumBenefits } from "../components/PremiumBenefits";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const Steps = ({ steps }) => (
  <div className="mb-5">
    <p className="text-[11px] uppercase tracking-wider text-amber-400/70 mb-2">Come si calcola</p>
    {steps.length === 0 ? (
      <p className="text-purple-300/60 text-xs">Il numero è già ridotto: non serve alcun passaggio.</p>
    ) : (
      <div className="flex flex-wrap gap-2">
        {steps.map((s, i) => (
          <span key={i} className="font-mono text-xs text-purple-200/80 bg-[#0f0617] border border-[#4a2175] rounded-lg px-3 py-1.5">{s}</span>
        ))}
      </div>
    )}
  </div>
);

const MeaningBlock = ({ title, meaning, shadow }) => (
  <div className="mb-5">
    <p className="text-[11px] uppercase tracking-wider text-amber-400/70 mb-1.5">Significato · {title}</p>
    <p className="text-purple-100/85 text-sm leading-relaxed">{meaning}</p>
    {shadow && <p className="text-purple-300/60 text-xs mt-2">Ombra: {shadow}</p>}
  </div>
);

const SectionBody = ({ s }) => {
  if (s.key === "maestri") {
    return (
      <div>
        <p className="text-purple-100/85 text-sm leading-relaxed italic mb-3">{s.interpretation}</p>
        <div className="space-y-3">
          {s.masters.map((m) => (
            <div
              key={m.number}
              className={`rounded-xl border p-4 ${m.present ? "border-amber-500/60 bg-amber-500/5" : "border-[#4a2175] bg-[#0f0617] opacity-80"}`}
              data-testid={`numerologia-master-${m.number}`}
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="font-display text-amber-100"><span className="gold-text text-xl mr-2">{m.number}</span>{m.title}</p>
                {m.present && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-300 border border-amber-500/50 rounded-full px-3 py-1 shrink-0">
                    Presente in questa data
                  </span>
                )}
              </div>
              <p className="text-purple-100/85 text-sm leading-relaxed">{m.text}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (s.key === "cicli") {
    return (
      <div>
        <p className="text-purple-100/85 text-sm leading-relaxed italic mb-3">{s.interpretation}</p>
        <div className="space-y-4">
          {s.cycles.map((c) => (
            <div key={c.label} className="rounded-xl border border-[#4a2175] bg-[#0f0617] p-4" data-testid={`numerologia-cycle-${c.label.replace(/\s/g, "-").toLowerCase()}`}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="font-display text-amber-100 text-sm">{c.label} <span className="text-purple-300/50 font-sans text-xs">· {c.source}</span></p>
                <p className="font-display text-2xl gold-text shrink-0">{c.number}</p>
              </div>
              <Steps steps={c.steps} />
              <MeaningBlock title={c.meaning_title} meaning={c.meaning} shadow={c.shadow} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="text-center mb-6">
        <p className="font-display text-5xl gold-text" data-testid={`numerologia-number-${s.key}`}>{s.number}</p>
        {s.is_master && (
          <span className="inline-block mt-2 text-[10px] uppercase tracking-wider text-amber-300 border border-amber-500/50 rounded-full px-3 py-1">
            Numero Maestro
          </span>
        )}
      </div>
      <Steps steps={s.steps} />
      <MeaningBlock title={s.meaning_title} meaning={s.meaning} shadow={s.shadow} />
      <div>
        <p className="text-[11px] uppercase tracking-wider text-amber-400/70 mb-1.5">Lettura</p>
        <p className="text-purple-100/85 text-sm leading-relaxed italic">{s.interpretation}</p>
      </div>
    </div>
  );
};

export default function Numerologia() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const archiveId = params.get("archivio");
  const [birthDate, setBirthDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(null);
  const [fromArchive, setFromArchive] = useState(false);
  const [open, setOpen] = useState(null);
  const [saveState, setSaveState] = useState("pending");
  const [saving, setSaving] = useState(false);
  const [daily, setDaily] = useState(null);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (user) {
      api.get("/entitlements").then((r) => setPlan(r.data.plan)).catch(() => setPlan("client"));
    }
  }, [user]);

  useEffect(() => {
    if (user && archiveId) {
      api.get(`/numerology/archive/${archiveId}`)
        .then((r) => { setReading(r.data.reading); setFromArchive(true); })
        .catch((e) => toast.error(fmtErr(e)));
    }
  }, [user, archiveId]);

  useEffect(() => {
    if (user) {
      api.get("/numerology/daily").then((r) => setDaily(r.data)).catch(() => {});
    }
  }, [user]);

  const toggleDaily = async () => {
    try {
      const r = await api.post("/numerology/daily", { enabled: !daily?.enabled });
      setDaily((d) => ({ ...d, enabled: r.data.enabled }));
      toast.success(r.data.enabled
        ? "Previsione quotidiana attivata: da domani mattina la ricevi con una notifica."
        : "Previsione quotidiana disattivata.");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (loading) return null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="numerologia-login-required">
        <div className="mystic-card p-10">
          <Hash className="w-10 h-10 text-amber-400/60 mx-auto mb-5" />
          <h1 className="font-display text-3xl text-amber-100 mb-4">Numerologia</h1>
          <p className="text-purple-200/70 text-sm mb-8">
            La numerologia è riservata agli abbonati di Arcana: accedi o crea un account per continuare.
          </p>
          <Link to="/accedi?redirect=/numerologia" data-testid="numerologia-login-btn" className="gold-btn inline-block px-8 py-3 text-sm">
            Accedi o registrati
          </Link>
        </div>
      </div>
    );
  }

  if (plan === null) return null;

  if (plan !== "admin" && plan !== "sub") {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="numerologia-premium-gate">
        <div className="mystic-card p-10">
          <Lock className="w-10 h-10 text-amber-400/60 mx-auto mb-5" />
          <h1 className="font-display text-3xl text-amber-100 mb-4">Numerologia</h1>
          <p className="text-purple-200/70 text-sm mb-8 leading-relaxed">
            La numerologia personale, la previsione quotidiana del tuo numero e l'archivio delle tue
            letture sono riservati esclusivamente agli abbonati di Arcana.
          </p>
          <PremiumBenefits />
          <Link to="/piani" data-testid="numerologia-premium-cta" className="gold-btn inline-block px-8 py-3 text-sm">
            Scopri l'abbonamento
          </Link>
        </div>
      </div>
    );
  }

  // Inserimento manuale gg/mm/aaaa: il calendario a comparsa era scomodo per date lontane
  const fmtDateIT = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 8);
    if (d.length > 4) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
    if (d.length > 2) return `${d.slice(0, 2)}/${d.slice(2)}`;
    return d;
  };
  const parseDateIT = (v) => {
    const m = v.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const [, g, mo, a] = m;
    const d = new Date(+a, +mo - 1, +g);
    if (d.getFullYear() !== +a || d.getMonth() !== +mo - 1 || d.getDate() !== +g) return null;
    if (+a < 1900 || d > new Date()) return null;
    return `${a}-${mo.padStart(2, "0")}-${g.padStart(2, "0")}`;
  };

  const calc = async () => {
    if (!birthDate) return;
    const iso = parseDateIT(birthDate);
    if (!iso) {
      toast.error("Data non valida: scrivi giorno, mese e anno (es. 20/03/1987).");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/numerology", { birth_date: iso });
      setReading(r.data);
      setFromArchive(false);
      setSaveState("pending");
      setOpen("percorso");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/numerology/archive", { birth_date: reading.birth_date });
      setSaveState("saved");
      toast.success("Numerologia custodita nel tuo archivio.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setReading(null);
    setFromArchive(false);
    setBirthDate("");
    setOpen(null);
    setSaveState("pending");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-16 sm:py-20" data-testid="numerologia-page">
      <div className="text-center mb-10">
        <p className="eyebrow">Il tuo quotidiano, custodito qui</p>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-100 mt-4">Numerologia</h1>
        <p className="text-purple-200/70 mt-5 text-sm max-w-xl mx-auto leading-relaxed">
          Inserisci una data di nascita — la tua o quella di chi vuoi — e Arcana calcolerà tutti i numeri
          della lettura. La data non viene salvata nel tuo account: resta solo tua.
        </p>
      </div>

      {daily?.eligible && (
        <div className="mystic-card p-6 sm:p-7 mb-8" data-testid="numerologia-daily">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-display text-amber-100 text-sm mb-1">Il tuo numero del giorno</p>
              <p className="text-purple-300/60 text-xs leading-relaxed">
                Ogni mattina Arcana calcola il tuo Giorno Personale e ti invia una breve previsione della
                giornata — non la spiegazione teorica del numero — con una notifica. Resta sempre
                consultabile nella sezione Notifiche.
              </p>
              {daily.enabled && !daily.has_push && (
                <p className="text-amber-300/80 text-[11px] mt-2">
                  Attiva le notifiche sul telefono per riceverla ogni mattina.
                </p>
              )}
            </div>
            <button
              onClick={toggleDaily}
              data-testid="numerologia-daily-toggle"
              className={`shrink-0 text-[10px] uppercase tracking-wider rounded-full px-4 py-2 border transition-colors ${daily.enabled ? "text-amber-300 border-amber-500/50 bg-amber-500/10" : "text-purple-300/60 border-[#4a2175] hover:border-amber-500/40"}`}
            >
              {daily.enabled ? "Attiva" : "Disattivata"}
            </button>
          </div>
          {daily.enabled && daily.today?.text && (
            <div className="mt-5 pt-5 border-t border-amber-500/15" data-testid="numerologia-daily-text">
              <p className="eyebrow mb-3">Oggi · numero del giorno {daily.today.number}</p>
              <p className="text-purple-100/85 text-sm leading-relaxed whitespace-pre-line">{daily.today.text}</p>
            </div>
          )}
          {daily.enabled && !daily.today && (
            <p className="mt-4 text-[11px] text-purple-300/50" data-testid="numerologia-daily-pending">
              La previsione di oggi è in preparazione: la riceverai con una notifica.
            </p>
          )}
        </div>
      )}

      {!reading ? (
        <div className="mystic-card p-7 sm:p-9" data-testid="numerologia-form">
          <label className="font-display text-amber-100 text-sm block mb-3">Data di nascita <span className="text-purple-300/50 font-sans text-xs">(giorno/mese/anno)</span></label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              inputMode="numeric"
              value={birthDate}
              onChange={(e) => setBirthDate(fmtDateIT(e.target.value))}
              placeholder="es. 20/03/1987"
              maxLength={10}
              data-testid="numerologia-birthdate-input"
              className="input-dark flex-1"
            />
            <button
              onClick={calc}
              disabled={busy || !birthDate}
              data-testid="numerologia-calc-btn"
              className="gold-btn px-8 py-2.5 text-xs disabled:opacity-50"
            >
              {busy ? "Un attimo…" : "Scopri la Numerologia"}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="text-center" data-testid="numerologia-result">
            {fromArchive && (
              <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-purple-300/70 border border-[#4a2175] rounded-full px-4 py-1.5 mb-3" data-testid="numerologia-archived-note">
                <Archive className="w-3 h-3" /> Dal tuo archivio
              </p>
            )}
            <h2 className="font-display text-3xl text-amber-100">La tua Numerologia</h2>
            <p className="text-purple-300/60 text-sm mt-2">
              Data di nascita: <span className="text-amber-200">{reading.birth_date_label}</span> · consultata il {reading.consultation_label}
            </p>
          </div>

          <div className="space-y-3" data-testid="numerologia-sections">
            {reading.sections.map((s) => (
              <div key={s.key} className="mystic-card overflow-hidden" data-testid={`numerologia-section-${s.key}`}>
                <button
                  onClick={() => setOpen(open === s.key ? null : s.key)}
                  data-testid={`numerologia-toggle-${s.key}`}
                  className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <span className="font-display text-amber-100">{s.title}</span>
                  <span className="flex items-center gap-3 shrink-0">
                    {s.number != null && <span className="font-display text-xl gold-text">{s.number}</span>}
                    <ChevronDown className={`w-4 h-4 text-purple-300/60 transition-transform duration-300 ${open === s.key ? "rotate-180" : ""}`} />
                  </span>
                </button>
                {open === s.key && (
                  <div className="px-5 pb-6 pt-1 border-t border-[#4a2175]/50">
                    <div className="pt-5"><SectionBody s={s} /></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {!fromArchive && saveState === "pending" && (
            <div className="mystic-card p-7 text-center" data-testid="numerologia-save-prompt">
              <p className="font-display text-amber-100 mb-6">Vuoi salvare questa numerologia nel tuo archivio?</p>
              <div className="flex items-center justify-center gap-4">
                <button onClick={save} disabled={saving} data-testid="numerologia-save-btn" className="gold-btn px-8 py-2.5 text-xs disabled:opacity-50">
                  {saving ? "Un attimo…" : "Salva"}
                </button>
                <button onClick={() => setSaveState("dismissed")} data-testid="numerologia-nosave-btn" className="text-xs text-purple-300/70 hover:text-amber-200 border border-[#4a2175] rounded-full px-6 py-2.5 transition-colors">
                  Non salvare
                </button>
              </div>
            </div>
          )}

          {saveState === "saved" && (
            <div className="mystic-card p-6 text-center" data-testid="numerologia-saved-note">
              <p className="text-purple-100/85 text-sm">
                Custodita nel tuo archivio: la ritrovi in{" "}
                <Link to="/letture" data-testid="numerologia-archive-link" className="text-amber-300 hover:text-amber-200 underline underline-offset-4 decoration-amber-500/40">Le mie letture</Link>.
              </p>
            </div>
          )}

          <div className="text-center">
            <button onClick={reset} data-testid="numerologia-reset-btn" className="inline-flex items-center gap-2 text-xs text-purple-300/70 hover:text-amber-200 transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Nuova numerologia
            </button>
          </div>
        </div>
      )}

      <p className="text-center text-purple-300/40 text-xs mt-10 flex items-center justify-center gap-1.5 max-w-xl mx-auto" data-testid="numerologia-disclaimer">
        <Sparkles className="w-3.5 h-3.5 shrink-0" />
        La numerologia è una pratica simbolica di riflessione personale: non una previsione né un metodo scientificamente dimostrato.
      </p>
    </div>
  );
}
